/**
 * Узел дерева навгации.
 * @publicApi
 */
import {DxNavigationTreeNodeDataProvider} from "./dx-navigation-tree-node-data-provider";


export class DxNavigationTreeNode {
  /**
   * Идентификатор данного навишационного узла.
   * Использование идентификаторов сильноупрощает жинь.
   * @name _id
   * @type {number}
   */
  private _id: number;

  /**
   * Строковый идент данного навишационного узла
   * Использование идентификаторов сильноупрощает жинь.
   * @name _ident
   * @type {string}
   */
  private _ident: string;

  /**
   * Порядок следования данного навишационного узла при отображении в рамках уровня.
   * @name _ord
   * @type {number}
   */
  private readonly _ord: number;

  /**
   * Название (лэйбл) данного навишационного узла.
   * @name _title
   * @type {string}
   */
  private _title: string;

  /**
   * Хинт, выводящийся при переходе фокуса на узел.
   * @name _hint
   * @type {string}
   */
  private readonly _hint: string;

  /**
   * Иконка узла, заданная относительным путем или в base64.
   * @name _icon
   * @type {string}
   */
  private readonly _icon: string;

  /**
   * Пока пусть будет название роута, который нужно открыть при клике по данному узлу.
   * Сам по себе маппинг каким компонентом какой роут открывать пусть живет в настроках роутинга,
   * как чисто прикладная вещь.
   * @name _route
   * @type {string}
   */
  private _route: string;


  /**
   * Уровень данного навишационного узла в дереве.
   * Уровеь вычисляется как уровень одителя + 1 при создании ноды.
   * @name _level
   * @type {number}
   */
  private _level: number;

  /**
   * Признак видим\скрыт узел.
   * @name _isVisible
   * @type {boolean} - true - видим, else - иначе.
   */
  private _isVisible: boolean = true;

  /**
   * Параметры, которые должны быть отражены в url после ? при открытии ноды.
   * Например http:\\<host>:<port>\app\navigator\event?id=100500.
   * @name _queryParameters
   * @type {Map<String, Object>}
   */
  private _queryParameters: Map<String, Object>;

  /**
   * Родительский узел данного навишационного узла
   * @name _parent
   * @type {DxNavigationTreeNode}
   */
  private readonly _parent: DxNavigationTreeNode;

  /**
   * Дочерние навигационные узлы.
   * @name _children
   * @type {DxNavigationTreeNode[]}
   */
  private _children: DxNavigationTreeNode[];

  /**
   * Признак наличия поддерва для данного узла.
   * Пояснение: впоминаем модель каталога с подразделами (иерархическими или нет).
   * Каждый подраздел в общем случае также может представлять собой иерархическую навигационную структуру.
   * Таким образом если узел имеет поддерево, то при клике по нему необходимо открыть боковую навигацию,
   * в которой будет отражена структура подраздела.
   * @name _hasSubTree
   * @type {boolean}
   */
  private readonly _hasSubTree: boolean = false;

  /**
   * Признак вовлечен ли данный нод в роутинг, или же он просто является группирующим узлом,
   * следовательно для него не должно быть комопнента и он не должен открываться через роутер.
   * @name _involvedInRouting
   * @type {boolean}
   */
  private readonly _involvedInRouting: boolean = true;


  /**
   * Создает узел дерева навигации.
   * @constructor
   * @param {DxNavigationTreeNodeDataProvider} nodeDataProvider - провайдер данных для узла.
   * @param {DxNavigationTreeNode} parent - родительский узел.
   */
  constructor(parent: DxNavigationTreeNode, nodeDataProvider?: DxNavigationTreeNodeDataProvider) {
    if(nodeDataProvider) {
      this._id = nodeDataProvider.getNodeId();
      this._ident = nodeDataProvider.getNodeIdent();
      this._ord = nodeDataProvider.getNodeOrd();
      this._title = nodeDataProvider.getNodeTitle();
      this._icon = nodeDataProvider.getNodeIcon();
      this._hint = nodeDataProvider.getNodeHint();
      this._route = nodeDataProvider.getNodeRoute();
      this._hasSubTree = nodeDataProvider.hasSubTree();
      this._involvedInRouting = nodeDataProvider.isInvolvedInRouting();
    }
    this._parent = parent;
    this._level = parent ? (parent.level + 1) : 0;
    this._children = [];
  }


  /** Генерация id нодов */
  public static generateNodeId(): number {
    return Math.round((new Date().getTime() / 1000) * Math.random());
  }

  public get nodeUrl(): string {
    const segments: string[] = [];
    segments.push(this._route);
    let parent: DxNavigationTreeNode = this._parent;
    while (parent && !parent.isRoot) {
      if(parent.involvedInRouting) {
        segments.push(parent.route);
      }
      parent = parent.parent;
    }
    return segments.reverse().join('/');
  }

  /**
   * Возвращает признак того, что данный узел является корнем дерева.
   * В целях упрощения работы дерево всегда имеем скрытый фиктивный корень с уровнем 0.
   * @return {boolean} - true - данный узел корень дерева, false - иначе.
   */
  public get isRoot(): boolean {
    return this._parent === null;
  }

  /**
   * Возвращает признак наличия детей у данного узла.
   * @return {boolean} - true - данный узел имеет детей, false - иначе.
   */
  public get hasChildren(): boolean {
    return this._children && this._children.length > 0;
  }

  /**
   * Возвращает признак того участвует\не участвует данный нод в роутинге.
   * @return {boolean} - true - данный узел вовлечен в ройтинг, false - иначе.
   */
  public get involvedInRouting(): boolean {
    return this._involvedInRouting;
  }

  /**
   * Возвращает признак того, что данный узел является листом дерева.
   * @return {boolean} - true - данный узел является листом, false - иначе.
   */
  public get isLeaf(): boolean {
    return !this.hasChildren;
  }

  get level(): number {
    return this._level;
  }

  set level(value: number) {
    this._level = value;
  }

  get id(): number {
    return this._id;
  }

  set id(value: number) {
    this._id = value;
  }

  get ident(): string {
    return this._ident;
  }

  set ident(value: string) {
    this._ident = value;
  }

  get ord(): number {
    return this._ord;
  }

  get title(): string {
    return this._title;
  }

  set title(value: string) {
    this._title = value;
  }

  get hint(): string {
    return this._hint;
  }

  get icon(): string {
    return this._icon;
  }

  get queryParameters(): Map<String, Object> {
    return this._queryParameters;
  }

  get parent(): DxNavigationTreeNode {
    return this._parent;
  }

  get children(): DxNavigationTreeNode[] {
    return this._children;
  }

  set children(value: DxNavigationTreeNode[]) {
    this._children = value;
  }

  get isVisible(): boolean {
    return this._isVisible;
  }

  set isVisible(value: boolean) {
    this._isVisible = value;
  }

  get hasSubTree(): boolean {
    return this._hasSubTree;
  }

  get route(): string {
    return this._route;
  }

  set route(value: string) {
    this._route = value;
  }
}
